import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuthStore } from '@/store/authStore';

export interface PermissionContextValue {
  can: (permission: string | string[]) => boolean;
  cannot: (permission: string | string[]) => boolean;
  hasRole: (role: string | string[]) => boolean;
  hasPermission: (permission: string | string[]) => boolean;
  hasAnyPermission: (permission: string[]) => boolean;
  hasFeature: (featureKey: string) => boolean;
  isSuperAdmin: boolean;
  permissions: string[];
  roles: string[];
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

/** Stable reference so the `roles` selector below never triggers a getSnapshot loop when logged out. */
const EMPTY_ROLES: string[] = [];

/** Wraps the app so any component can read permission state via usePermission(). */
export function PermissionProvider({ children }: { children: ReactNode }) {
  const permissions = useAuthStore((s) => s.permissions);
  const roles = useAuthStore((s) => s.user?.roles ?? EMPTY_ROLES);
  const hasRole = useAuthStore((s) => s.hasRole);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const hasFeature = useAuthStore((s) => s.hasFeature);
  const can = useAuthStore((s) => s.can);
  const cannot = useAuthStore((s) => s.cannot);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin());

  const value = useMemo<PermissionContextValue>(
    () => ({
      can,
      cannot,
      hasRole,
      hasPermission,
      hasAnyPermission,
      hasFeature,
      isSuperAdmin,
      permissions,
      roles,
    }),
    [can, cannot, hasRole, hasPermission, hasAnyPermission, hasFeature, isSuperAdmin, permissions, roles],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissionContext(): PermissionContextValue {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error('usePermissionContext must be used within a <PermissionProvider>');
  }
  return ctx;
}
